import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { Helmet } from 'react-helmet';
import moment from 'moment';
import parser from 'html-react-parser';
import { isEmpty } from 'lodash-es';
import { Badge, Inline } from 'chaoskit/src/components';

import { Icon } from '../../components';
import { getArticleType } from '../../../utils/helpers';
import ChaosMonkeyLayout from '../../layouts/ChaosMonkeyLayout';
import ChaosMonkeyResources from './Resources';
import ChaosMonkeyHero from './Hero';
import ArticleFooter from './ArticleFooter';

const ArticleContent = ({
  alternatives,
  data: {
    article: {
      frontmatter: {
        title,
        dateModified,
        seoTitle,
        titleSub,
        description,
        hideTableOfContents,
        heroImage,
        socialMediaImage,
        includeResources,
        hideReadingTime,
      },
      fields: { heroContrast },
      headings,
      tableOfContents,
      html,
      timeToRead,
      fileAbsolutePath,
    },
  },
  pageContext: { nodeType, previous, next },
}) => {
  const getArticleNavigation = () => {
    if (!isEmpty(previous) || !isEmpty(next)) {
      return {
        previous: !isEmpty(previous) && previous.fields.slug,
        next: !isEmpty(next) && next.fields.slug,
      };
    }

    return null;
  };

  const renderTableOfContents = !hideTableOfContents && headings.length;

  const contentColumnClasses = cx({
    'column-9@medium': renderTableOfContents,
    'column-12': !renderTableOfContents,
  });
  const articleHeaderClasses = cx('chaosMonkey__article-header', {
    'u-textCenter': !renderTableOfContents,
  });
  const headerInlineClass = !renderTableOfContents ? 'u-flexCenter' : null;
  // If we don't have a hero background set; fallback to alternative
  const heroSrc =
    heroImage ||
    'https://res.cloudinary.com/gremlin/image/upload/c_limit,q_80,w_2000/ChaosMonkey-Alternatives-Hero.svg';
  const heroLinkClasses = cx({
    'u-linkDefault u-linkUnderline': !heroContrast,
    'u-link': heroContrast,
  });

  return (
    <ChaosMonkeyLayout contrast={heroContrast}>
      <Helmet
        title={seoTitle || title}
        meta={[
          { name: 'description', content: description },
          {
            property: 'og:image',
            content:
              socialMediaImage ||
              'https://res.cloudinary.com/gremlin/image/upload/c_limit,q_80,w_2000/ChaosMonkeyAlternatives.png',
          },
          { name: 'node-type', content: nodeType },
        ]}
      />
      <ChaosMonkeyHero align="top" background={heroSrc} contrast={heroContrast}>
        <div className="hero-content">
          <div className="container">
            <Inline className="u-textSmall">
              <a className={heroLinkClasses} href="/chaos-monkey/">
                Chaos Monkey
              </a>
              {alternatives && (
                <Fragment>
                  <Icon
                    size="small"
                    className="u-textMuted"
                    icon="caret-right"
                  />
                  <a
                    className={heroLinkClasses}
                    href="/chaos-monkey/chaos-monkey-alternatives/"
                  >
                    Chaos Monkey Alternatives
                  </a>
                </Fragment>
              )}
              <Icon size="small" className="u-textMuted" icon="caret-right" />
              <span>{title}</span>
            </Inline>
          </div>
        </div>
      </ChaosMonkeyHero>
      <main className="chaosMonkey__base u-bgChaosMonkeyLight">
        <section className="section">
          <div className="container">
            <article className="chaosMonkey__article">
              <div className="row row--largeGutter">
                {renderTableOfContents ? (
                  <div className="column-3@medium u-hiddenDown@small">
                    <aside
                      className="site__toc"
                      dangerouslySetInnerHTML={{ __html: tableOfContents }}
                    />
                  </div>
                ) : null}
                <div className={contentColumnClasses}>
                  <div className={articleHeaderClasses}>
                    <h5 className="u-textFluid--medium u-textChaosMonkey">
                      {alternatives ? 'Chaos Monkey Alternatives' : title}
                    </h5>
                    <h1 className="u-textFluid--h2-h3 u-mt--remove u-mb--large">
                      {alternatives ? title : titleSub}
                    </h1>
                    <Inline className={headerInlineClass}>
                      {!hideReadingTime && (
                        <Badge
                          className="badge--chaosMonkey"
                          label={`${timeToRead} min read`}
                        />
                      )}
                      <div className="u-textSmall u-textMuted">
                        Last Updated {moment(dateModified).format('LL')}
                      </div>
                    </Inline>
                  </div>
                  <div className="chaosMonkey__article-content has-anchorBump">
                    {parser(html)}
                    {includeResources && <ChaosMonkeyResources />}
                  </div>
                </div>
              </div>
              <ArticleFooter
                // Stubbing in structure for `node` so we can still use destructering above and only need the `fileAbsolutePath` field anyway
                type={getArticleType({ fileAbsolutePath })}
                navigation={getArticleNavigation()}
              />
            </article>
          </div>
        </section>
      </main>
    </ChaosMonkeyLayout>
  );
};

ArticleContent.propTypes = {
  alternatives: PropTypes.bool,
  data: PropTypes.object.isRequired,
  pageContext: PropTypes.object.isRequired,
};

export default ArticleContent;
